import { SLIDER_CONFIG, SELECTORS } from './Constants';
import type {
    SlickConfig,
    SlickInstance,
    RangeInfo,
    ArticleRange,
    NavigationButtons,
    SlickInitHandler,
    SlickAfterChangeHandler
} from './Types';

/**
 * Slider Manager
 * Handles Slick slider initialization, reinitialization, and cleanup
 */
export default class SliderManager {
    articleSlides: JQuery<HTMLElement>;
    slickConfig: SlickConfig;
    articlesContainer: JQuery<HTMLElement>;
    component: JQuery<HTMLElement>;
    retryTimeout: ReturnType<typeof setTimeout> | null;

    /**
     * @param articleSlides - Container for article slides
     * @param slickConfig - Slick configuration object
     * @param articlesContainer - Parent articles container
     * @param component - Main component container
     */
    constructor(
        articleSlides: JQuery<HTMLElement>,
        slickConfig: SlickConfig,
        articlesContainer: JQuery<HTMLElement>,
        component: JQuery<HTMLElement>
    ) {
        this.articleSlides = articleSlides;
        this.slickConfig = slickConfig;
        this.articlesContainer = articlesContainer;
        this.component = component;
        this.retryTimeout = null;
    }

    /**
     * Initializes slider with retry logic for async DOM readiness
     * @param tries - Number of retry attempts remaining
     * @returns Whether initialization succeeded
     */
    init(tries: number = SLIDER_CONFIG.INIT_MAX_RETRIES): boolean {
        // Clear any pending retry
        if (this.retryTimeout) {
            clearTimeout(this.retryTimeout);
            this.retryTimeout = null;
        }

        if (this._canInitialize()) {
            this._doInitialize();
            return true;
        }

        if (tries <= 0) {
            return false;
        }

        // Schedule retry
        this.retryTimeout = setTimeout(() => {
            this.retryTimeout = null;
            this.init(tries - 1);
        }, SLIDER_CONFIG.INIT_RETRY_DELAY);

        return false;
    }

    /**
     * Reinitializes slider after configuration changes
     */
    reinit(): void {
        this.destroy();

        const articleCount = this.articleSlides.children('article').length;
        if (articleCount === 0) {
            return;
        }

        // Defer initialization to ensure layout has settled
        const runInit = (): void => {
            this.init();

            try {
                this.articleSlides.slick('setPosition');
                $(window).trigger('resize');
            } catch {
                // Ignore positioning and resize errors
            }
        };

        if (typeof window.requestAnimationFrame === 'function') {
            requestAnimationFrame(() => setTimeout(runInit, 0));
        } else {
            setTimeout(runInit, 50);
        }
    }

    /**
     * Destroys slider instance and cleans up DOM
     */
    destroy(): void {
        if (this._isInitialized() && typeof this.articleSlides.slick === 'function') {
            try {
                this.articleSlides.slick('unslick');
            } catch {
                // Ignore unslick errors
            }
        }

        // Clean up any leftover Slick wrappers
        this._cleanupSlickWrappers();

        this.articleSlides.removeClass('slick-initialized slick-slider');
    }

    /**
     * Rebuilds article container DOM for clean reinitialization
     */
    rebuildContainer(): JQuery<HTMLElement> {
        const articles = this.articleSlides.find('article');
        const currentClasses = this.articleSlides.attr('class') || '';
        const freshContainer = $('<div />').attr('class', currentClasses);

        articles.appendTo(freshContainer);
        this.articleSlides.replaceWith(freshContainer);
        this.articleSlides = freshContainer;

        return this.articleSlides;
    }

    /**
     * Checks if slider is initialized
     * @returns Whether slider is initialized
     */
    private _isInitialized(): boolean {
        return this.articleSlides.hasClass('slick-initialized');
    }

    /**
     * Checks if slider can be initialized
     * @returns Whether slider can be initialized
     */
    private _canInitialize(): boolean {
        const articleCount = this.articleSlides.children('article').length;
        const containerWidth = this.articlesContainer.width() ?? 0;
        const componentWidth = this.component.width() ?? 0;

        return articleCount > 0 &&
               containerWidth >= SLIDER_CONFIG.MIN_WIDTH_THRESHOLD &&
               componentWidth >= SLIDER_CONFIG.MIN_WIDTH_THRESHOLD;
    }

    private _doInitialize(): void {
        // Apply accessibility enhancements if available
        if (typeof window.slickSliderA11y === 'function') {
            window.slickSliderA11y(this.articleSlides);
        }

        // Initialize Slick
        if (typeof this.articleSlides.slick === 'function') {
            this.articleSlides.slick(this.slickConfig);

            // Set up aria-label updates for navigation buttons
            this._setupAriaLabelUpdates();
        }
    }

    private _setupAriaLabelUpdates(): void {
        // Update aria-labels on initialization
        const initHandler: SlickInitHandler = (_event: JQuery.TriggeredEvent, slick: SlickInstance): void => {
            this._updateNavigationAriaLabels(slick);
        };
        this.articleSlides.on('init', initHandler);

        // Update aria-labels after each slide change
        const afterChangeHandler: SlickAfterChangeHandler = (_event: JQuery.TriggeredEvent, slick: SlickInstance, _currentSlide: number): void => {
            this._updateNavigationAriaLabels(slick);
        };
        this.articleSlides.on('afterChange', afterChangeHandler);

        // Initial update if already initialized
        if (this._isInitialized()) {
            const slickInstance = this.articleSlides.slick('getSlick');
            this._updateNavigationAriaLabels(slickInstance);
        }
    }

    /**
     * Updates aria-labels on prev/next buttons with article range information
     * @param slick - Slick instance
     */
    private _updateNavigationAriaLabels(slick: SlickInstance): void {
        if (!slick) return;

        const rangeInfo = this._calculateArticleRanges(slick);
        const buttons = this._getNavigationButtons();

        this._updatePreviousButtonLabels(buttons.prev, rangeInfo);
        this._updateNextButtonLabels(buttons.next, rangeInfo);
    }

    /**
     * Calculates article ranges for current, next, and previous pages
     * @param slick - Slick instance
     * @returns Range information
     */
    private _calculateArticleRanges(slick: SlickInstance): RangeInfo {
        const articlesPerPage = (slick.options.slidesToShow || 1) * (slick.options.rows || 1);
        const totalArticles = this._countTotalArticles();
        const currentSlide = slick.currentSlide;
        const slideCount = slick.slideCount ?? 0;

        const prevSlide = (currentSlide - 1 + slideCount) % slideCount;
        const nextSlide = (currentSlide + 1) % slideCount;

        return {
            current: this._calculateRangeForSlide(currentSlide, articlesPerPage, totalArticles),
            prev: this._calculateRangeForSlide(prevSlide, articlesPerPage, totalArticles),
            next: this._calculateRangeForSlide(nextSlide, articlesPerPage, totalArticles),
            total: totalArticles
        };
    }

    /**
     * Counts total articles in the slider
     * @returns Total article count
     */
    private _countTotalArticles(): number {
        const componentContent = this.component.closest(SELECTORS.COMPONENT_CONTENT);
        const numArticles = parseInt(componentContent.attr('data-num-articles') ?? '0', 10) || 0;
        const hasComponentBody = componentContent.attr('data-comp-body') === 'true';

        return hasComponentBody ? numArticles : numArticles - 1;
    }

    /**
     * Calculates article range for a specific slide
     * @param slideIndex - Slide index
     * @param articlesPerPage - Articles shown per page
     * @param totalArticles - Total articles in slider
     * @returns First and last visible article numbers
     */
    private _calculateRangeForSlide(slideIndex: number, articlesPerPage: number, totalArticles: number): ArticleRange {
        const first = (slideIndex * articlesPerPage) + 1;
        const last = Math.min(first + articlesPerPage - 1, totalArticles);
        return { first, last };
    }

    /**
     * Gets appropriate navigation buttons based on viewport
     * @returns Previous and next button elements
     */
    private _getNavigationButtons(): NavigationButtons {
        const isMobile = window.matchMedia(SLIDER_CONFIG.MOBILE_BREAKPOINT).matches;

        return {
            prev: isMobile
                ? this.component.find(SELECTORS.MOBILE_PREV_ARROW)
                : this.component.find(SELECTORS.PREV_ARROW),
            next: isMobile
                ? this.component.find(SELECTORS.MOBILE_NEXT_ARROW)
                : this.component.find(SELECTORS.NEXT_ARROW)
        };
    }

    /**
     * Updates previous button aria-label
     * @param button - Previous button element
     * @param rangeInfo - Article range information
     */
    private _updatePreviousButtonLabels(button: JQuery<HTMLElement>, rangeInfo: RangeInfo): void {
        if (!button.length) return;

        const isMobileButton = button.hasClass('slick-prev-mobile-carousel');
        if (isMobileButton) return;

        const ariaLabel = this._formatPrevAriaLabel(rangeInfo);
        button.attr('aria-label', ariaLabel);
    }

    /**
     * Updates next button aria-label
     * @param button - Next button element
     * @param rangeInfo - Article range information
     */
    private _updateNextButtonLabels(button: JQuery<HTMLElement>, rangeInfo: RangeInfo): void {
        if (!button.length) return;

        const isMobileButton = button.hasClass('slick-next-mobile-carousel');
        if (isMobileButton) return;

        const ariaLabel = this._formatNextAriaLabel(rangeInfo);
        button.attr('aria-label', ariaLabel);
    }

    /**
     * Formats aria-label for previous button (desktop only)
     * @param rangeInfo - Article range information
     * @returns Formatted aria-label
     */
    private _formatPrevAriaLabel(rangeInfo: RangeInfo): string {
        const { prev, total } = rangeInfo;

        if (prev.first === prev.last) {
            return `Previous article: ${prev.first} of ${total}`;
        }

        return `Previous articles: ${prev.first} to ${prev.last} of ${total}`;
    }

    /**
     * Formats aria-label for next button (desktop only)
     * @param rangeInfo - Article range information
     * @returns Formatted aria-label
     */
    private _formatNextAriaLabel(rangeInfo: RangeInfo): string {
        const { next, total } = rangeInfo;

        if (next.first === next.last) {
            return `Next article: ${next.first} of ${total}`;
        }

        return `Next articles: ${next.first} to ${next.last} of ${total}`;
    }

    private _cleanupSlickWrappers(): void {
        const slickList = this.articleSlides.children('.slick-list');

        if (slickList.length) {
            const slickTrack = slickList.children('.slick-track');

            if (slickTrack.length) {
                slickTrack.children().appendTo(this.articleSlides);
            } else {
                slickList.children().appendTo(this.articleSlides);
            }

            slickList.remove();
        }
    }
}

